import buildInvoicePreviewUrl from "helpers/buildInvoicePreviewUrl";
import {
  mapPackagingComponentFromApi,
  mapPackagingLabelToImporter,
  mapPackagingLabelToManufacturer,
  mapPackagingLabelToRepresentative,
  mapPackagingLabelToTraceability,
  resolveComponentTypeLabel,
  resolveMaterialDisplay,
  resolveSortingInstructionLabel,
  formatWeightWithUnit,
} from "./packagingCompositionData";

const displayText = (value) => String(value ?? "").trim();

const formatGrossWeightDisplay = (value, unit) =>
  formatWeightWithUnit(value, unit);

/** Canonical digital link from backend qr_code. */
export const resolvePackagingLabelDigitalUrl = (qrCode) => {
  const raw = displayText(qrCode);
  if (!raw) return null;
  try {
    return new URL(raw).toString();
  } catch {
    return null;
  }
};

export const resolvePackagingLabelMediaUrl = (path) => {
  if (path == null || String(path).trim() === "") return null;
  return buildInvoicePreviewUrl(path) || null;
};

/**
 * Collect recycling icons from label.components[].recycling_code_icons.
 * Preserves component then icon order; dedupes by code (first wins).
 */
export function mapRecyclingCodeIconsFromLabel(currentLabel) {
  const components = currentLabel?.components;
  if (!Array.isArray(components) || components.length === 0) {
    return [];
  }

  const seenCodes = new Set();
  const result = [];

  components.forEach((component) => {
    const icons = component?.recycling_code_icons;
    if (!Array.isArray(icons) || icons.length === 0) return;

    icons.forEach((item) => {
      const code = displayText(item?.code);
      if (!code || seenCodes.has(code)) return;
      seenCodes.add(code);

      const iconRaw = displayText(item?.icon);
      const iconUrl = iconRaw
        ? resolvePackagingLabelMediaUrl(iconRaw) || iconRaw
        : null;

      result.push({
        code,
        iconUrl,
      });
    });
  });

  return result;
}

/**
 * Build presentational props for PpwrPrintableCompactLabel from wizard state.
 */
export function buildPpwrPrintableLabelProps({
  traceability,
  manufacturer,
  representativeEnabled,
  representativeMode,
  customRepresentative,
  importerEnabled,
  importer,
  packagingComponents,
  selects,
  resolveCountryLabel,
  representativeTypeOptions = [],
  qrCodeImageUrl = null,
  qrCodeValue = null,
  currentLabel = null,
}) {
  const countryLabel =
    typeof resolveCountryLabel === "function"
      ? resolveCountryLabel
      : (code) => displayText(code);

  const lappaLabel =
    (Array.isArray(representativeTypeOptions)
      ? representativeTypeOptions
      : []
    ).find((option) => String(option?.value) === "lappa")?.label || "Lappa";

  const manufacturerName =
    displayText(manufacturer?.businessName) ||
    displayText(manufacturer?.companyName);
  const manufacturerAddress = [
    displayText(manufacturer?.streetAddress),
    [displayText(manufacturer?.postalCode), displayText(manufacturer?.city)]
      .filter(Boolean)
      .join(" "),
    countryLabel(manufacturer?.country),
  ]
    .filter(Boolean)
    .join(", ");

  const productName = displayText(traceability?.product);
  const productLot = displayText(traceability?.batchLotNumber);
  const productSize = displayText(traceability?.size);
  const productGrossWeight = formatGrossWeightDisplay(
    traceability?.grossWeight,
    traceability?.grossWeightUnit
  );

  const representativeFromLabel = currentLabel
    ? mapPackagingLabelToRepresentative(currentLabel)
    : null;
  const resolvedRepresentativeEnabled = representativeFromLabel
    ? representativeFromLabel.enabled
    : Boolean(representativeEnabled);
  const resolvedRepresentativeMode = representativeFromLabel
    ? representativeFromLabel.mode
    : representativeMode;
  const resolvedCustomRepresentative = representativeFromLabel
    ? representativeFromLabel.custom
    : customRepresentative;

  let euRepresentativeName = "";
  let euRepresentativeAddress = "";
  let euRepresentativeEmail = "";
  if (resolvedRepresentativeEnabled) {
    if (resolvedRepresentativeMode === "lappa") {
      euRepresentativeName = lappaLabel;
    } else {
      euRepresentativeName = displayText(
        resolvedCustomRepresentative?.companyName
      );
      euRepresentativeAddress = [
        displayText(resolvedCustomRepresentative?.streetAddress),
        [
          displayText(resolvedCustomRepresentative?.postalCode),
          displayText(resolvedCustomRepresentative?.city),
        ]
          .filter(Boolean)
          .join(" "),
        countryLabel(resolvedCustomRepresentative?.country),
      ]
        .filter(Boolean)
        .join(", ");
      euRepresentativeEmail = displayText(
        resolvedCustomRepresentative?.email
      );
    }
  }

  const showEuRepresentative =
    Boolean(resolvedRepresentativeEnabled) &&
    Boolean(
      euRepresentativeName || euRepresentativeAddress || euRepresentativeEmail
    );

  const manufacturerEmail = displayText(manufacturer?.email);
  const importerName = displayText(importer?.companyName);
  const importerAddress = [
    displayText(importer?.streetAddress),
    [displayText(importer?.postalCode), displayText(importer?.city)]
      .filter(Boolean)
      .join(" "),
    countryLabel(importer?.country),
  ]
    .filter(Boolean)
    .join(", ");
  const importerEmail = displayText(importer?.email);
  const showImporter = Boolean(importerEnabled);

  const recyclingCodeIcons = mapRecyclingCodeIconsFromLabel(currentLabel);

  const labelComponents = Array.isArray(currentLabel?.components)
    ? currentLabel.components.map((component) =>
        component &&
        typeof component === "object" &&
        Object.prototype.hasOwnProperty.call(component, "componentType")
          ? component
          : mapPackagingComponentFromApi(component)
      )
    : null;

  const resolvedComponents = (
    labelComponents != null ? labelComponents : packagingComponents || []
  ).map((component, index) => {
      const componentName = displayText(
        resolveComponentTypeLabel(component, selects?.componentTypes)
      );
      const materialLabel = displayText(
        resolveMaterialDisplay(
          component,
          selects?.materialFamilies,
          selects?.materialSubtypesByFamily
        )
      );
      const sortingLabel = displayText(
        resolveSortingInstructionLabel(
          component,
          selects?.sortingInstructions
        )
      );
      return {
        id: component.id || `component-${index}`,
        index,
        componentName,
        materialLabel,
        sortingLabel,
      };
    });

  return {
    productName,
    productLot,
    productSize,
    productGrossWeight,
    manufacturerName,
    manufacturerAddress,
    manufacturerEmail,
    showEuRepresentative,
    euRepresentativeName,
    euRepresentativeAddress,
    euRepresentativeEmail,
    showImporter,
    importerName,
    importerAddress,
    importerEmail,
    qrCodeImageUrl: qrCodeImageUrl || null,
    qrCodeValue: qrCodeValue || null,
    resolvedComponents,
    recyclingCodeIcons,
  };
}

export function buildPpwrPrintableLabelPropsFromApi({
  label,
  components,
  selects,
  resolveCountryLabel,
}) {
  const mappedTraceability = mapPackagingLabelToTraceability(label);
  const mappedManufacturer = mapPackagingLabelToManufacturer(label);
  const representative = mapPackagingLabelToRepresentative(label);
  const importerMapped = mapPackagingLabelToImporter(label);
  const mappedComponents = (Array.isArray(components) ? components : []).map(
    (component) =>
      component &&
      typeof component === "object" &&
      Object.prototype.hasOwnProperty.call(component, "componentType")
        ? component
        : mapPackagingComponentFromApi(component)
  );

  const qrCodeImageRaw =
    label?.qr_code_image != null && String(label.qr_code_image).trim() !== ""
      ? String(label.qr_code_image).trim()
      : null;
  const qrCodeImageUrl = qrCodeImageRaw
    ? resolvePackagingLabelMediaUrl(qrCodeImageRaw) || qrCodeImageRaw
    : null;
  const qrCodeValue = resolvePackagingLabelDigitalUrl(label?.qr_code);

  return buildPpwrPrintableLabelProps({
    traceability: mappedTraceability,
    manufacturer: mappedManufacturer,
    representativeEnabled: representative.enabled,
    representativeMode: representative.mode,
    customRepresentative: representative.custom,
    importerEnabled: importerMapped.enabled,
    importer: importerMapped.custom,
    packagingComponents: mappedComponents,
    selects,
    resolveCountryLabel,
    qrCodeImageUrl,
    qrCodeValue,
    currentLabel: label,
  });
}
